import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import { EmployeeReviewStatus } from '../employeeReviewStatus';
import {
  useGetEmployeeReviewCommandAvailabilityQuery,
  useSetEmployeeReviewFeedbackDoneMutation,
} from '../api/employeeReviewApi';
import { extractErrorMessage } from '../../../utils/errorUtils';

type EmployeeReviewFeedbackDoneControlProps = {
  reviewId: number;
  disabled?: boolean;
  onStatusChanged: (status: number) => void;
  onSuccess: (message: string) => void;
};

export const EmployeeReviewFeedbackDoneControl = ({
  reviewId,
  disabled = false,
  onStatusChanged,
  onSuccess,
}: EmployeeReviewFeedbackDoneControlProps) => {
  const availability = useGetEmployeeReviewCommandAvailabilityQuery(reviewId, {
    skip: !Number.isInteger(reviewId) || reviewId <= 0,
    refetchOnMountOrArgChange: true,
  });
  const [setFeedbackDone, setFeedbackDoneState] = useSetEmployeeReviewFeedbackDoneMutation();
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isFinished = availability.data?.isFeedbackDone ?? false;
  const isBusy =
    disabled || availability.isLoading || availability.isFetching || setFeedbackDoneState.isLoading;
  const canChange = availability.data?.canSetFeedbackDone ?? false;

  const closeConfirmation = () => {
    if (!setFeedbackDoneState.isLoading) setPendingValue(null);
  };

  const confirm = async () => {
    if (pendingValue === null) return;
    setError(null);
    try {
      const result = await setFeedbackDone({ reviewId, isFeedbackDone: pendingValue }).unwrap();
      const status = EmployeeReviewStatus[result.status as keyof typeof EmployeeReviewStatus];
      if (typeof status !== 'number') {
        throw new Error('Feedback completion response returned an unknown status.');
      }
      onStatusChanged(status);
      onSuccess(result.message);
      setPendingValue(null);
    } catch (requestError) {
      setError(extractErrorMessage(requestError, 'Failed to update feedback completion.'));
    }
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={isFinished}
            disabled={isBusy || !canChange}
            onChange={(event) => setPendingValue(event.target.checked)}
          />
        }
        label="Feedback Done"
      />
      <Dialog open={pendingValue !== null} onClose={closeConfirmation} maxWidth="xs" fullWidth>
        <DialogTitle>{pendingValue ? 'Finish review?' : 'Reopen review?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingValue
              ? 'Finished reviews no longer accept participant feedback until reopened.'
              : 'The review will accept participant feedback again.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmation} disabled={setFeedbackDoneState.isLoading}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={setFeedbackDoneState.isLoading} variant="contained">
            {pendingValue ? 'Finish' : 'Reopen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
